import React, { MutableRefObject, useCallback, useEffect, useReducer, useState } from 'react'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import { errorToast, infoToast, successToast } from '../../util/Util'
import ConfirmButtonModal from '../../components/ConfirmButtonModal'
import Dots from '../../icons/Dots'
import TimeText from '../../components/TimeText'
import {
  formatExternalAccountDetails,
  formatExternalAccountType,
  Section,
  SectionContent,
  SectionItem,
  SubTitle
} from '../../components/Shared'
import Radom, { ExternalAccount } from '../../state/Radom'
import { genSectionItems } from '../../util/SectionItems'
import { RADOM_COLORS } from '../../util/Constants'
import Spinner from '../../components/Spinner'
import { PrimaryButton } from '../../components/Button'
import { isCryptoExternalAccount } from '../../util/ExternalAccounts'

interface CustomerPayoutsAccountsProps {
  customerId: string
  isLoading: boolean
  onRefreshRef?: MutableRefObject<(() => void) | null>
  onCreateAccount?: () => void
}

const CustomerPayoutsAccounts = ({
  customerId,
  isLoading: parentLoading,
  onRefreshRef,
  onCreateAccount
}: CustomerPayoutsAccountsProps): JSX.Element => {
  const [closeDropdownFns, updateCloseDropdownFns] = useReducer((p: any, n: any) => ({ ...p, ...n }), {})
  const [externalAccounts, setExternalAccounts] = useState<ExternalAccount[]>([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)


  const fetchAccounts = useCallback(async () => {
    if (!customerId) return
    setIsLoadingAccounts(true)
    try {
      const accounts = await Radom.listExternalAccounts(customerId)
      setExternalAccounts(accounts)
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message || 'Failed to load accounts')
    }
    setIsLoadingAccounts(false)
  }, [customerId])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  // Expose refresh function to parent via ref
  useEffect(() => {
    if (onRefreshRef) {
      onRefreshRef.current = fetchAccounts
    }
    return () => {
      if (onRefreshRef) {
        onRefreshRef.current = null
      }
    }
  }, [onRefreshRef, fetchAccounts])

  const handleDeleteAccount = async (accountId: string): Promise<void> => {
    try {
      await Radom.deleteExternalAccount(accountId)
      successToast('Account deleted')
      fetchAccounts()
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message || 'Failed to delete account')
    }
  }

  const isLoading = parentLoading || isLoadingAccounts

  if (isLoading) {
    return <div style={{
      padding: 40,
      display: 'flex',
      justifyContent: 'center'
    }}>
      <Spinner />
    </div>
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------------------------------------------------

  const renderAccount = (account: ExternalAccount, idx: number): JSX.Element => (
    <Section
      key={account.id}
      style={{ position: 'relative' }}
    >
      <SectionContent numColumns={4}>
        {genSectionItems([
          { heading: 'Owner', content: account.accountOwnerName },
          { heading: 'Type', content: formatExternalAccountType(account) },
          { heading: 'Details', content: formatExternalAccountDetails(account) },
          <SectionItem key={idx}>
            <SubTitle>Created at</SubTitle>
            <TimeText date={new Date(account.createdAt)} />
          </SectionItem>
        ], 4)}

        <div style={{
          position: 'absolute',
          top: 20,
          right: 20
        }} onClick={e => e.stopPropagation()}>
          <Dropdown
            noChevron
            onCloseFn={f => updateCloseDropdownFns({ [account.id]: f })}
            iconTrigger
            selectedContent={<Dots width={15} />}
            dropdownContent={
              <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                <DropdownItem onClick={() => {
                  navigator.clipboard.writeText(account.id)
                  infoToast('Account ID copied to clipboard')
                  closeDropdownFns[account.id]?.()
                }}>Copy account ID</DropdownItem>
                <ConfirmButtonModal
                  title='Delete account'
                  description='Are you sure you want to delete this account?'
                  action='Delete account'
                  onConfirm={async () => { await handleDeleteAccount(account.id) }}
                  ActionComponent={(props: any) => <DropdownItem {...props}>Delete account</DropdownItem>}
                />
              </div>
            }
          />
        </div>
      </SectionContent>
    </Section>
  )

  // -------------------------------------------------------------------------------------------------------------------
  // Empty state
  // -------------------------------------------------------------------------------------------------------------------

  if (externalAccounts.length === 0) {
    return (
      <div style={{
        padding: 40,
        textAlign: 'center',
        fontSize: 14,
        color: RADOM_COLORS.GRAY4
      }}>
        No payout accounts yet.{' '}
        {onCreateAccount && (
          <a
            href="#"
            style={{
              color: '#4C7BF4',
              textDecoration: 'none',
              fontWeight: 500,
              cursor: 'pointer'
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onCreateAccount()
            }}
          >
            Create one
          </a>
        )}
      </div>
    )
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Accounts list
  // -------------------------------------------------------------------------------------------------------------------

  const cryptoAccounts = externalAccounts.filter(isCryptoExternalAccount)
  const fiatAccounts = externalAccounts.filter(a => !isCryptoExternalAccount(a))

  return (
    <>
      {onCreateAccount && (
        <PrimaryButton style={{ width: 250, alignSelf: 'end' }} onClick={onCreateAccount}>
          Create payout account
        </PrimaryButton>
      )}
      {cryptoAccounts.length > 0 && (
        <>
          <SubTitle style={{ fontSize: 16 }}>Crypto</SubTitle>
          {cryptoAccounts.map((account, idx) => renderAccount(account, idx))}
        </>
      )}

      {fiatAccounts.length > 0 && (
        <Section>
          <SubTitle style={{ fontSize: 18 }}>Fiat Payout Accounts</SubTitle>
          {fiatAccounts.map((account, idx) => renderAccount(account, idx))}
        </Section>
      )}
    </>
  )
}

export default CustomerPayoutsAccounts
